import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCircle2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/axios";

const toneClass = {
    danger: "bg-red-500",
    warning: "bg-amber-500",
    celebrate: "bg-emerald-500",
    info: "bg-[var(--accent-primary)]",
};

const getNotificationCacheKey = () => {
    const token = localStorage.getItem("employee_token") || "guest";
    return `employee_notifications_cache_v2_${token.slice(-12)}`;
};

const normalizeNotificationData = (value) => {
    const items = Array.isArray(value?.items) ? value.items : [];
    return {
        ...value,
        count: items.filter((item) => !item.read).length,
        items,
    };
};

const readNotificationCache = () => {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(getNotificationCacheKey()) || "{}");
        return normalizeNotificationData(parsed.data || { count: 0, items: [] });
    } catch {
        sessionStorage.removeItem(getNotificationCacheKey());
        return { count: 0, items: [] };
    }
};

const writeNotificationCache = (nextData) => {
    try {
        sessionStorage.setItem(getNotificationCacheKey(), JSON.stringify({
            data: normalizeNotificationData(nextData),
            savedAt: Date.now(),
        }));
    } catch {
        // Notifications still work without cache.
    }
};

const normalizePath = (value = "") => {
    const path = String(value || "").split("?")[0].replace(/\/+$/, "");
    return path || "/";
};

const NotificationBell = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const wrapRef = useRef(null);
    const inFlightRef = useRef(false);
    const autoReadInFlightRef = useRef(new Set());
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(() => readNotificationCache());

    const setNotificationData = useCallback((nextData) => {
        const normalized = normalizeNotificationData(nextData);
        setData(normalized);
        writeNotificationCache(normalized);
    }, []);

    const fetchNotifications = useCallback(async (options = {}) => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        if (!options.silent) setLoading(true);
        try {
            const res = await api.get("/api/employee/notifications", {
                cache: false,
                params: { _t: Date.now() },
            });
            setNotificationData(res.data || { count: 0, items: [] });
        } catch (err) {
            console.error("Failed to load notifications:", err);
        } finally {
            inFlightRef.current = false;
            setLoading(false);
        }
    }, [setNotificationData]);

    useEffect(() => {
        fetchNotifications({ silent: true });
        const id = window.setInterval(() => fetchNotifications({ silent: true }), 10000);
        return () => window.clearInterval(id);
    }, [fetchNotifications]);

    useEffect(() => {
        if (open) fetchNotifications({ silent: true });
    }, [fetchNotifications, open]);

    useEffect(() => {
        const handleFocus = () => fetchNotifications({ silent: true });
        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleFocus);
        return () => {
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener("visibilitychange", handleFocus);
        };
    }, [fetchNotifications]);

    useEffect(() => {
        const handleClick = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const markRead = useCallback(async (item) => {
        if (!item?.id || item.read) return;

        setData((current) => {
            const nextItems = (current.items || []).map((entry) =>
                entry.id === item.id ? { ...entry, read: true, readAt: new Date().toISOString() } : entry,
            );
            const nextData = {
                ...current,
                count: nextItems.filter((entry) => !entry.read).length,
                items: nextItems,
            };
            writeNotificationCache(nextData);
            return nextData;
        });

        try {
            await api.patch("/api/employee/notifications/read", { notificationId: item.id }, { skipSaveToast: true });
        } catch (err) {
            console.error("Failed to mark notification read:", err);
        }
    }, []);

    const markAllRead = useCallback(async () => {
        const unreadIds = (data.items || []).filter((item) => !item.read).map((item) => item.id);
        if (!unreadIds.length) return;

        setData((current) => {
            const nextData = {
                ...current,
                count: 0,
                items: (current.items || []).map((item) => ({ ...item, read: true, readAt: item.readAt || new Date().toISOString() })),
            };
            writeNotificationCache(nextData);
            return nextData;
        });

        try {
            await api.patch("/api/employee/notifications/read-all", { notificationIds: unreadIds }, { skipSaveToast: true });
        } catch (err) {
            console.error("Failed to mark all notifications read:", err);
        }
    }, [data.items]);

    const markMatchingRouteRead = useCallback(async () => {
        const currentPath = normalizePath(location.pathname);
        const matchingUnreadIds = (data.items || [])
            .filter((item) => !item.read && item.route && normalizePath(item.route) === currentPath)
            .map((item) => item.id)
            .filter((id) => !autoReadInFlightRef.current.has(id));

        if (!matchingUnreadIds.length) return;

        matchingUnreadIds.forEach((id) => autoReadInFlightRef.current.add(id));

        setData((current) => {
            const nextData = {
                ...current,
                items: (current.items || []).map((item) =>
                    matchingUnreadIds.includes(item.id)
                        ? { ...item, read: true, readAt: item.readAt || new Date().toISOString() }
                        : item,
                ),
            };
            nextData.count = nextData.items.filter((item) => !item.read).length;
            writeNotificationCache(nextData);
            return nextData;
        });

        try {
            await api.patch("/api/employee/notifications/read-all", { notificationIds: matchingUnreadIds }, { skipSaveToast: true });
        } catch (err) {
            console.error("Failed to auto mark notifications read:", err);
        } finally {
            matchingUnreadIds.forEach((id) => autoReadInFlightRef.current.delete(id));
        }
    }, [data.items, location.pathname]);

    useEffect(() => {
        markMatchingRouteRead();
    }, [markMatchingRouteRead]);

    const handleItem = (item) => {
        markRead(item);
        setOpen(false);
        if (item.route) navigate(item.route);
    };

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                className="relative w-11 h-11 rounded-2xl border border-[var(--border-color)] bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 flex items-center justify-center transition-all"
                title="Notifications"
            >
                <Bell size={18} />
                {data.count > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center">
                        {data.count > 9 ? "9+" : data.count}
                    </span>
                )}
            </button>

            {open && (
                <div className="fixed left-3 right-3 top-[60px] z-[120] max-h-[calc(100vh-5rem)] overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl sm:absolute sm:left-auto sm:right-0 sm:top-[52px] sm:w-[320px] sm:max-w-[calc(100vw-2rem)]">
                    <div className="flex items-start justify-between gap-3 border-b border-[var(--border-color)] p-3 sm:items-center sm:p-4">
                        <div className="min-w-0">
                            <p className="text-xs font-bold uppercase text-[var(--accent-primary)]">Notifications</p>
                            <h3 className="font-bold">{data.count} unread</h3>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                            {data.count > 0 && (
                                <button type="button" onClick={markAllRead} className="text-xs font-bold text-[var(--accent-primary)]">
                                    Mark all read
                                </button>
                            )}
                            <button type="button" onClick={fetchNotifications} className="text-xs font-bold text-[var(--text-secondary)]">
                                {loading ? "Loading" : "Refresh"}
                            </button>
                        </div>
                    </div>
                    <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-2 sm:max-h-[360px]">
                        {!data.items?.length ? (
                            <div className="p-5 text-center text-sm text-[var(--text-secondary)]">
                                <CheckCircle2 size={22} className="mx-auto mb-2 text-emerald-500" />
                                All clear
                            </div>
                        ) : (
                            data.items.map((item, index) => (
                                <button
                                    key={`${item.id}-${index}`}
                                    type="button"
                                    onClick={() => handleItem(item)}
                                    className={`flex w-full gap-3 rounded-xl p-3 text-left transition-all hover:bg-[var(--bg-secondary)] ${item.read ? "opacity-60" : "bg-[var(--bg-secondary)]/40"}`}
                                >
                                    <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${item.read ? "bg-slate-300" : toneClass[item.tone] || toneClass.info}`} />
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-center gap-2">
                                            <span className="block min-w-0 truncate text-sm font-bold">{item.title}</span>
                                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black ${item.read ? "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" : "bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-200"}`}>
                                                {item.read ? "Read" : "Unread"}
                                            </span>
                                        </span>
                                        <span className="block text-xs text-[var(--text-secondary)] line-clamp-2">{item.body}</span>
                                    </span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
